import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { generateReportPDF } from "@/knowmeno/KnowMenoApp";
import { listLabUploads } from "@/lib/labs/labs.functions";

// ============================================================
// DEV TOOL — Preview MY actual Symptom Intelligence Report
// ============================================================
// Bypasses the Day-14 gate and generates a Symptom Intelligence Report PDF for
// the SIGNED-IN founder, using:
//   - their real profile (from localStorage, synced from `profiles`)
//   - their real lab uploads (server fn `listLabUploads`, RLS-scoped
//     to auth.uid via accessToken — same path the Care tab uses)
//
// This is the same code path as the production user PDF — no overrides
// to the rendering logic. Only the gate is bypassed.
// ============================================================

const DEV_TOOLS_ENABLED = true;
const FOUNDER_EMAIL_RE = /^founders(\+[^@]+)?@(knowmeno|meno-iq)\.com$/i;
const USER_KEY = "menotrack_user";

export const Route = createFileRoute("/dev/preview-my-report")({
  component: PreviewMyReportPage,
});

function PreviewMyReportPage() {
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setEmail(data.user?.email ?? null);
      setLoading(false);
    });
  }, []);

  if (loading) return <Shell>Loading…</Shell>;
  if (!DEV_TOOLS_ENABLED) return <Shell>403 — dev tools disabled.</Shell>;
  if (!email || !FOUNDER_EMAIL_RE.test(email)) {
    return (
      <Shell>
        403 — not authorised.
        {email && (
          <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
            Signed in as {email}
          </div>
        )}
      </Shell>
    );
  }

  const handleGenerate = async () => {
    setBusy(true);
    setMsg(null);
    try {
      // 1. Real profile — read from localStorage (synced by ProfileSync).
      const raw = window.localStorage.getItem(USER_KEY);
      if (!raw) throw new Error("No profile found in storage. Open the app first to hydrate.");
      const user = JSON.parse(raw);

      // 2. Real labs — same server fn the Care tab uses (RLS by user_id).
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) throw new Error("No access token — please sign in again.");
      const rows = await listLabUploads({ data: { accessToken } });

      // 3. Map DB rows → LabUpload shape (mirrors MyCare in KnowMenoApp).
      const labUploads = (rows || []).map((r: any) => {
        const d = new Date(r.lab_date + "T00:00:00");
        const dispDate = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        const mk = (v: any, u: string) =>
          v == null ? undefined : { value: Number(v), unit: u };
        return {
          date: dispDate,
          estradiol: mk(r.estradiol_value, r.estradiol_unit || "pg/mL"),
          fsh: mk(r.fsh_value, r.fsh_unit || "mIU/mL"),
          lh: mk(r.lh_value, r.lh_unit || "mIU/mL"),
          progesterone: mk(r.progesterone_value, r.progesterone_unit || "ng/mL"),
          tsh: mk(r.tsh_value, r.tsh_unit || "µIU/mL"),
        };
      });

      // 4. Days tracked — derived from trial start (best available proxy
      //    without pulling daily_logs). Falls back to 14 so the report
      //    renders all sections even on Day 1.
      const signup = user?.signupDate ? new Date(user.signupDate) : null;
      const daysTracked = signup
        ? Math.max(14, Math.ceil((Date.now() - signup.getTime()) / 86400000))
        : 14;

      // 5. Generate — same call signature as production.
      const clean = new URLSearchParams(window.location.search).get("clean") === "1";
      await generateReportPDF(user, daysTracked, [], { labUploads, clean });

      setMsg(
        `Report opened in new window. Pulled ${labUploads.length} lab upload${
          labUploads.length === 1 ? "" : "s"
        } from your account.`
      );
    } catch (e: any) {
      setMsg(`Error: ${e?.message || String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <h1 style={{ fontSize: 22, fontWeight: 500, margin: "0 0 8px" }}>
        Preview my actual Symptom Intelligence Report
      </h1>
      <p style={{ color: "#6b6a64", fontSize: 14, lineHeight: 1.55, margin: "0 0 18px" }}>
        Bypasses the Day-14 gate and generates the production Symptom Intelligence Report
        PDF using <strong style={{ color: "#2b2b28", fontWeight: 500 }}>your</strong>{" "}
        real profile and lab uploads (signed in as {email}). Same code path as
        the live user PDF — only the gate is bypassed.
      </p>
      <button
        onClick={handleGenerate}
        disabled={busy}
        style={{
          padding: "12px 22px",
          borderRadius: 12,
          background: busy ? "#9bbfae" : "#1D9E75",
          color: "#fff",
          border: "none",
          fontSize: 14,
          fontWeight: 500,
          cursor: busy ? "wait" : "pointer",
        }}
      >
        {busy ? "Generating…" : "Preview my actual report (PDF) →"}
      </button>
      {msg && (
        <p style={{ fontSize: 13, color: "#2b2b28", marginTop: 18, lineHeight: 1.55 }}>
          {msg}
        </p>
      )}
      <p style={{ fontSize: 12, color: "#888", marginTop: 18, lineHeight: 1.55 }}>
        Opens in a new window. Use your browser's <em>Save as PDF</em> from the
        print dialog to download. Cycle data is omitted in this preview (the
        report falls back to its built-in placeholder for cycles); labs and
        profile are pulled live from your account.
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FAF8F3",
        padding: "60px 24px",
        fontFamily: "-apple-system, system-ui, sans-serif",
        color: "#2b2b28",
      }}
    >
      <div
        style={{
          maxWidth: 560,
          margin: "0 auto",
          background: "#fff",
          padding: "32px 28px",
          borderRadius: 18,
          border: "1px solid #e8e6df",
        }}
      >
        {children}
      </div>
    </div>
  );
}
